import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../../i18n/LanguageContext';

const AUDIT_API_URL = process.env.REACT_APP_AUDIT_API_URL;

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-CH', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const maxValue = (items) => items.reduce((highest, item) => Math.max(highest, Number(item.value || 0)), 0);
const getBarWidth = (value, max) => {
  if (!value || value <= 0 || !max) return 0;
  return Math.max((Number(value) / Math.max(Number(max), 1)) * 100, 0);
};

const PIE_COLORS = ['#1B3047', '#14b8a6', '#d97706', '#8b5cf6', '#f97316', '#10b981', '#ef4444', '#0ea5e9'];

const buildPieStyle = (items) => {
  const filtered = (items || []).filter((item) => Number(item.value || 0) > 0);
  if (!filtered.length) {
    return { background: 'linear-gradient(135deg, #edf2f7 0%, #e2e8f0 100%)' };
  }

  const total = filtered.reduce((sum, item) => sum + Number(item.value || 0), 0);
  let start = 0;
  const segments = filtered.map((item, index) => {
    const size = total > 0 ? (Number(item.value || 0) / total) * 100 : 0;
    const end = start + size;
    const color = PIE_COLORS[index % PIE_COLORS.length];
    const segment = `${color} ${start}% ${end}%`;
    start = end;
    return segment;
  });

  return {
    background: `conic-gradient(${segments.join(', ')})`,
  };
};

/**
 * Parses request_data JSON safely whether it arrives as an object or a string.
 */
const parseRequestData = (data) => {
  if (!data) return {};
  if (typeof data === 'object') return data;
  try {
    return JSON.parse(data);
  } catch {
    return {};
  }
};

export default function SiteStatisticsManager() {
  const { strings } = useLanguage();
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Table state filters
  const [selectedEventType, setSelectedEventType] = useState('ALL');
  const [logSearchQuery, setLogSearchQuery] = useState('');
  const [logLimit, setLogLimit] = useState(50);

  const fetchAuditLogs = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(AUDIT_API_URL + '/audit', {
        method: 'GET',
        credentials: 'include',
      });
      const result = await response.json();
      
      if (!response.ok || !result.ok) {
        throw new Error(result.error || `HTTP error! status: ${response.status}`);
      }

      setAuditLogs(result.results || []);
    } catch (err) {
      setError(err.message || 'Unable to load audit log statistics.');
      setAuditLogs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAuditLogs();
  }, [fetchAuditLogs]);

  // Aggregate Insights Computation
  const insights = useMemo(() => {
    const eventCounts = {};
    const searchQueries = {};
    const emailedList = {};
    const visitedWebsites = {};
    const viewedProfessionals = {};
    const pageViews = {};

    auditLogs.forEach((log) => {
      const type = log.event_type || log.eventType || 'unknown';
      const reqData = parseRequestData(log.request_data || log.requestData);
      const createdAt = log.created_at || log.createdAt || log.timestamp;

      // 1. Total count per event type
      eventCounts[type] = (eventCounts[type] || 0) + 1;

      // 2. Search queries
      if (type === 'directory.search_executed' || reqData.searchQuery) {
        const query = (reqData.searchQuery || '').trim();
        if (query) {
          searchQueries[query] = (searchQueries[query] || 0) + 1;
        }
      }

      // 3. Email contacts sent
      if (type === 'directory.contact_email_click' || reqData.email) {
        const email = reqData.email || 'Unknown Email';
        const targetId = log.target_id || log.targetId || reqData.targetId || 'N/A';
        if (!emailedList[email]) {
          emailedList[email] = {
            email,
            targetId,
            count: 0,
            lastSent: createdAt,
          };
        }
        emailedList[email].count += 1;
        if (new Date(createdAt) > new Date(emailedList[email].lastSent)) {
          emailedList[email].lastSent = createdAt;
        }
      }

      // 4. Outbound Websites visited
      if (type === 'directory.website_visit_click' || reqData.website) {
        const website = reqData.website || 'Unknown URL';
        if (!visitedWebsites[website]) {
          visitedWebsites[website] = {
            website,
            count: 0,
            lastVisited: createdAt,
          };
        }
        visitedWebsites[website].count += 1;
        if (new Date(createdAt) > new Date(visitedWebsites[website].lastVisited)) {
          visitedWebsites[website].lastVisited = createdAt;
        }
      }

      // 5. Professional profiles viewed / interacted
      if (type === 'directory.record_view' || (log.target_entity === 'professionals' && log.target_id)) {
        const profId = log.target_id || log.targetId || reqData.targetId;
        if (profId) {
          if (!viewedProfessionals[profId]) {
            viewedProfessionals[profId] = {
              profId,
              count: 0,
              lastViewed: createdAt,
            };
          }
          viewedProfessionals[profId].count += 1;
          if (new Date(createdAt) > new Date(viewedProfessionals[profId].lastViewed)) {
            viewedProfessionals[profId].lastViewed = createdAt;
          }
        }
      }

      // 6. Page Views (pages.view)
      if (type === 'pages.view' || reqData.pageName) {
        const page = (reqData.pageName || 'Unknown').trim();
        pageViews[page] = (pageViews[page] || 0) + 1;
      }
    });

    // Formatting for charts
    const topSearchesFormatted = Object.entries(searchQueries)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);

    const eventTypesPie = Object.entries(eventCounts)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value);

    const topWebsitesFormatted = Object.values(visitedWebsites)
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    const topEmailedFormatted = Object.values(emailedList)
      .sort((a, b) => b.count - a.count);

    const topProfessionalsFormatted = Object.values(viewedProfessionals)
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    const pageViewsFormatted = Object.entries(pageViews)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value);

    return {
      totalLogs: auditLogs.length,
      totalSearches: Object.values(searchQueries).reduce((a, b) => a + b, 0),
      totalEmailsSent: Object.values(emailedList).reduce((sum, item) => sum + item.count, 0),
      totalWebsitesVisited: Object.values(visitedWebsites).reduce((sum, item) => sum + item.count, 0),
      totalProfileViews: Object.values(viewedProfessionals).reduce((sum, item) => sum + item.count, 0),
      eventTypesPie,
      topSearchesFormatted,
      topWebsitesFormatted,
      topEmailedFormatted,
      topProfessionalsFormatted,
      pageViewsFormatted,
    };
  }, [auditLogs]);

  // KPI Definition List
  const kpis = [
    { key: 'totalLogs', label: 'Total Audit Events', value: insights.totalLogs, className: 'theme-navy' },
    { key: 'totalSearches', label: 'Search Queries Executed', value: insights.totalSearches, className: 'theme-mint' },
    { key: 'totalProfileViews', label: 'Professional Views', value: insights.totalProfileViews, className: 'theme-gold' },
    { key: 'totalEmailsSent', label: 'Contact Emails Initiated', value: insights.totalEmailsSent, className: 'theme-rose' },
    { key: 'totalWebsitesVisited', label: 'Outbound Website Clicks', value: insights.totalWebsitesVisited, className: 'theme-indigo' },
  ];

  // Filtered raw logs table feed
  const filteredLogs = useMemo(() => {
    return auditLogs
      .filter((log) => {
        const type = log.event_type || log.eventType || '';
        const reqData = JSON.stringify(parseRequestData(log.request_data || log.requestData)).toLowerCase();
        const matchesType = selectedEventType === 'ALL' || type === selectedEventType;
        const matchesQuery = !logSearchQuery || 
          type.toLowerCase().includes(logSearchQuery.toLowerCase()) || 
          reqData.includes(logSearchQuery.toLowerCase()) ||
          String(log.target_id || '').includes(logSearchQuery);

        return matchesType && matchesQuery;
      })
      .slice(0, logLimit);
  }, [auditLogs, selectedEventType, logSearchQuery, logLimit]);

  const uniqueEventTypes = useMemo(() => {
    const types = new Set(auditLogs.map((log) => log.event_type || log.eventType).filter(Boolean));
    return ['ALL', ...Array.from(types)];
  }, [auditLogs]);

  return (
    <div className="admin-view-wrapper">
      <div className="admin-view-header">
        <h2 className="admin-title">Site Statistics & Audit Insights</h2>
      </div>

      {error && <div className="admin-error">{error}</div>}

      {loading ? (
        <div className="admin-empty-state">Loading site statistics & audit logs…</div>
      ) : auditLogs.length === 0 ? (
        <div className="admin-empty-state">No audit events recorded yet.</div>
      ) : (
        <>
          {/* KPI Summary Cards */}
          <div className="dashboard-kpis">
            {kpis.map((item) => (
              <div key={item.key} className={`dashboard-kpi-card ${item.className}`}>
                <span className="dashboard-kpi-label">{item.label}</span>
                <strong className="dashboard-kpi-value">
                  {new Intl.NumberFormat('en-CH').format(item.value || 0)}
                </strong>
              </div>
            ))}
          </div>

          {/* Grid Section 1: Event Distributions & Top Search Terms */}
          <div className="dashboard-grid">
            {/* Event Distribution Pie */}
            <section className="dashboard-panel">
              <div className="dashboard-panel-header">
                <h3>Event Type Breakdown</h3>
              </div>
              {insights.eventTypesPie.length === 0 ? (
                <div className="admin-empty-state compact">No event data available.</div>
              ) : (
                <div className="dashboard-pie-section">
                  <div className="dashboard-pie-chart" style={buildPieStyle(insights.eventTypesPie)} />
                  <div className="dashboard-pie-legend">
                    {insights.eventTypesPie.map((entry, index) => (
                      <div key={entry.label} className="dashboard-pie-legend-item">
                        <span
                          className="dashboard-pie-swatch"
                          style={{ background: PIE_COLORS[index % PIE_COLORS.length] }}
                        />
                        <span>{entry.label}</span>
                        <strong>{entry.value}</strong>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>

            {/* Top Search Queries Bar Chart */}
            <section className="dashboard-panel">
              <div className="dashboard-panel-header">
                <h3>Popular Directory Search Queries</h3>
              </div>
              <div className="chart-bars">
                {insights.topSearchesFormatted.length === 0 ? (
                  <div className="admin-empty-state compact">No search query logs found.</div>
                ) : (
                  insights.topSearchesFormatted.map((entry) => {
                    const width = getBarWidth(entry.value, maxValue(insights.topSearchesFormatted));
                    return (
                      <div key={entry.label} className="chart-bar-row">
                        <span style={{ fontWeight: 600 }}>"{entry.label}"</span>
                        <div className="chart-bar-track">
                          <div className="chart-bar-fill theme-bar-mint" style={{ width: `${width}%` }} />
                        </div>
                        <strong>{entry.value}</strong>
                      </div>
                    );
                  })
                )}
              </div>
            </section>
          </div>

          {/* Section 2: Page Views Bar Chart */}
          <section className="dashboard-panel" style={{ marginTop: '24px' }}>
            <div className="dashboard-panel-header">
              <h3>Page Views Breakdown (pages.view)</h3>
            </div>
            <div className="chart-bars">
              {insights.pageViewsFormatted.length === 0 ? (
                <div className="admin-empty-state compact">No page view events recorded.</div>
              ) : (
                insights.pageViewsFormatted.map((entry) => {
                  const width = getBarWidth(entry.value, maxValue(insights.pageViewsFormatted));
                  return (
                    <div key={entry.label} className="chart-bar-row">
                      <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{entry.label}</span>
                      <div className="chart-bar-track">
                        <div className="chart-bar-fill theme-bar-indigo" style={{ width: `${width}%` }} />
                      </div>
                      <strong>{entry.value} views</strong>
                    </div>
                  );
                })
              )}
            </div>
          </section>

          {/* Section 3: Contacted Professionals & Website Visits */}
          <div className="dashboard-grid" style={{ marginTop: '24px' }}>
            {/* Direct Emails Sent Table */}
            <section className="dashboard-panel">
              <div className="dashboard-panel-header">
                <h3>Emailed Professionals / Inquiries</h3>
              </div>
              <div className="dashboard-list">
                {insights.topEmailedFormatted.length === 0 ? (
                  <div className="admin-empty-state compact">No emails recorded in audit logs.</div>
                ) : (
                  insights.topEmailedFormatted.map((item) => (
                    <div key={item.email} className="dashboard-list-item">
                      <div>
                        <strong>{item.email}</strong>
                        {item.targetId !== 'N/A' && (
                          <span style={{ fontSize: '11px', color: '#64748b', display: 'block' }}>
                            Target ID: #{item.targetId}
                          </span>
                        )}
                      </div>
                      <div className="dashboard-list-meta">
                        <span>{item.count} {item.count === 1 ? 'inquiry' : 'inquiries'}</span>
                        <span>{formatDate(item.lastSent)}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </section>

            {/* Outbound Website Clicks */}
            <section className="dashboard-panel">
              <div className="dashboard-panel-header">
                <h3>Outbound Website Clicks</h3>
              </div>
              <div className="dashboard-list">
                {insights.topWebsitesFormatted.length === 0 ? (
                  <div className="admin-empty-state compact">No outbound site clicks recorded.</div>
                ) : (
                  insights.topWebsitesFormatted.map((item) => (
                    <div key={item.website} className="dashboard-list-item">
                      <span style={{ wordBreak: 'break-all', maxWidth: '60%' }}>
                        <a href={item.website} target="_blank" rel="noopener noreferrer" style={{ color: '#1B3047' }}>
                          {item.website}
                        </a>
                      </span>
                      <div className="dashboard-list-meta">
                        <span>{item.count} clicks</span>
                        <span>{formatDate(item.lastVisited)}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </section>
          </div>

          {/* Section 4: Most Viewed Professionals */}
          <section className="dashboard-panel" style={{ marginTop: '24px' }}>
            <div className="dashboard-panel-header">
              <h3>Most Viewed Professional Profiles</h3>
            </div>
            <div className="chart-bars">
              {insights.topProfessionalsFormatted.length === 0 ? (
                <div className="admin-empty-state compact">No profile views recorded.</div>
              ) : (
                insights.topProfessionalsFormatted.map((entry) => {
                  const width = getBarWidth(entry.count, maxValue(insights.topProfessionalsFormatted.map(p => ({ value: p.count }))));
                  return (
                    <div key={entry.profId} className="chart-bar-row">
                      <span>Professional #{entry.profId}</span>
                      <div className="chart-bar-track">
                        <div className="chart-bar-fill theme-bar-gold" style={{ width: `${width}%` }} />
                      </div>
                      <strong>{entry.count} views</strong>
                    </div>
                  );
                })
              )}
            </div>
          </section>

          {/* Section 5: Tabular Query & Event Feed */}
          <section className="dashboard-panel" style={{ marginTop: '24px' }}>
            <div className="dashboard-panel-header" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '12px' }}>
              <h3>Audit Event Activity Feed</h3>
              
              {/* Table Controls */}
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', width: '100%' }}>
                <input
                  type="text"
                  placeholder="Filter by query, email, or data..."
                  value={logSearchQuery}
                  onChange={(e) => setLogSearchQuery(e.target.value)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '4px',
                    border: '1px solid #cbd5e1',
                    fontSize: '13px',
                    flex: '1',
                    minWidth: '200px'
                  }}
                />

                <select
                  value={selectedEventType}
                  onChange={(e) => setSelectedEventType(e.target.value)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '4px',
                    border: '1px solid #cbd5e1',
                    fontSize: '13px'
                  }}
                >
                  {uniqueEventTypes.map((type) => (
                    <option key={type} value={type}>
                      {type === 'ALL' ? 'All Event Types' : type}
                    </option>
                  ))}
                </select>

                <select
                  value={logLimit}
                  onChange={(e) => setLogLimit(Number(e.target.value))}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '4px',
                    border: '1px solid #cbd5e1',
                    fontSize: '13px'
                  }}
                >
                  <option value={25}>Show 25</option>
                  <option value={50}>Show 50</option>
                  <option value={100}>Show 100</option>
                </select>
              </div>
            </div>

            {/* Structured Table */}
            <div style={{ overflowX: 'auto', marginTop: '12px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0', color: '#475569' }}>
                    <th style={{ padding: '10px' }}>Timestamp</th>
                    <th style={{ padding: '10px' }}>Event Type</th>
                    <th style={{ padding: '10px' }}>Target Entity / ID</th>
                    <th style={{ padding: '10px' }}>Request Details / Query</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLogs.length === 0 ? (
                    <tr>
                      <td colSpan={4} style={{ padding: '16px', textAlign: 'center', color: '#94a3b8' }}>
                        No audit events match the selected criteria.
                      </td>
                    </tr>
                  ) : (
                    filteredLogs.map((log, idx) => {
                      const reqData = parseRequestData(log.request_data || log.requestData);
                      return (
                        <tr key={log.id || idx} style={{ borderBottom: '1px solid #edf2f7' }}>
                          <td style={{ padding: '10px', whiteSpace: 'nowrap', color: '#64748b' }}>
                            {formatDate(log.created_at || log.createdAt || log.timestamp)}
                          </td>
                          <td style={{ padding: '10px', fontWeight: 600, color: '#1B3047' }}>
                            {log.event_type || log.eventType}
                          </td>
                          <td style={{ padding: '10px', color: '#334155' }}>
                            {log.target_entity || log.targetEntity ? `${log.target_entity || log.targetEntity} #${log.target_id || log.targetId}` : '—'}
                          </td>
                          <td style={{ padding: '10px', color: '#334155' }}>
                            {reqData.searchQuery && <div><strong>Search:</strong> "{reqData.searchQuery}"</div>}
                            {reqData.email && <div><strong>Email:</strong> {reqData.email}</div>}
                            {reqData.website && <div><strong>Website:</strong> {reqData.website}</div>}
                            {reqData.pageName && <div><strong>Page:</strong> {reqData.pageName}</div>}
                            {!reqData.searchQuery && !reqData.email && !reqData.website && !reqData.pageName && (
                              <code style={{ fontSize: '11px', background: '#f1f5f9', padding: '2px 4px', borderRadius: '3px' }}>
                                {JSON.stringify(reqData)}
                              </code>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}